import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/authContext";
import api from '../../Api';

// Status configurations
const statusConfig = {
  0: { label: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' },
  1: { label: 'Confirmed', color: '#10b981', bgColor: '#d1fae5', icon: '✓' },
  9: { label: 'Cancelled', color: '#ef4444', bgColor: '#fee2e2', icon: '✕' }
};

const Bookings = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/classBooking/api/orders/getOrders/', {
          withCredentials: true,
        });
        setBookings(res.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser]);

  // Not logged in state
  if (!currentUser) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>My Bookings</PageTitle>
        </PageHeader>
        <NotLoggedInContainer>
          <NotLoggedInCard>
            <NotLoggedInIcon>📋</NotLoggedInIcon>
            <NotLoggedInTitle>Your Bookings</NotLoggedInTitle>
            <NotLoggedInText>
              Sign in to view and manage your class bookings, check payment status, and access your booking history.
            </NotLoggedInText>
            <NotLoggedInFeatures>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>View all your bookings</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>Track payment status</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>Access QR codes for attendance</FeatureText>
              </FeatureItem>
            </NotLoggedInFeatures>
            <NotLoggedInButtons>
              <Link to="/en/login" style={{ width: '100%', textDecoration: 'none' }}>
                <PrimaryButton>Sign In</PrimaryButton>
              </Link>
              <Link to="/en/signup" style={{ width: '100%', textDecoration: 'none' }}>
                <SecondaryButton>Create Account</SecondaryButton>
              </Link>
            </NotLoggedInButtons>
          </NotLoggedInCard>
        </NotLoggedInContainer>
      </PageContainer>
    );
  }

  // Empty state
  if (!loading && bookings.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>My Bookings</PageTitle>
        </PageHeader>
        <EmptyStateContainer>
          <EmptyStateCard>
            <EmptyStateIconWrapper>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 9H21" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 21V9" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </EmptyStateIconWrapper>
            <EmptyStateTitle>No Bookings Yet</EmptyStateTitle>
            <EmptyStateText>
              Start exploring our classes and programs to find something you'll love!
            </EmptyStateText>
            <Link to="/en/" style={{ width: '100%', textDecoration: 'none' }}>
              <ExploreButton>
                <span>Explore Classes</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ExploreButton>
            </Link>
          </EmptyStateCard>
        </EmptyStateContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>My Bookings</PageTitle>
        <BookingCount>{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</BookingCount>
      </PageHeader>

      <BookingsList>
        {bookings.map((booking, index) => {
          const orderDetails = booking.order_details;
          const participantInfo = booking.participant_info;
          const status = statusConfig[booking.status] || statusConfig[0];

          return (
            <BookingCard 
              key={booking.id || index}
              onClick={() => navigate(`/en/order/${booking.token}`)}
            >
              {/* Status Badge */}
              <StatusBadge $color={status.color} $bgColor={status.bgColor}>
                {booking.status === 1 ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : booking.status === 0 ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                )}
                <span>{status.label}</span>
              </StatusBadge>

              {/* Main Content */}
              <CardHeader>
                <ProgramName>{orderDetails?.programName}</ProgramName>
                <OrderId>#{booking.id}</OrderId>
              </CardHeader>

              {orderDetails?.option1value && (
                <OptionTag>{orderDetails.option1value}</OptionTag>
              )}

              <CardDetails>
                <DetailRow>
                  <DetailIcon>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M20 21V19C20 16.7909 18.2091 15 16 15H8C5.79086 15 4 16.7909 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </DetailIcon>
                  <DetailText>{participantInfo?.firstname} {participantInfo?.lastname}</DetailText>
                </DetailRow>

                <DetailRow>
                  <DetailIcon>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </DetailIcon>
                  <DetailText>
                    {new Date(booking.order_date).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </DetailText>
                </DetailRow>
              </CardDetails>

              <CardFooter>
                <PriceTag>
                  {booking.price === 0 || booking.price === "0" ? 'Free' : `HK$${booking.price}`}
                </PriceTag>
                <ViewButton>
                  <span>View</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ViewButton>
              </CardFooter>
            </BookingCard>
          );
        })}
      </BookingsList>
    </PageContainer>
  );
};

export default Bookings;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding-bottom: 40px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const PageTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const BookingCount = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  padding: 6px 12px;
  border-radius: 20px;
`;

const BookingsList = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BookingCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.$bgColor};
  color: ${props => props.$color};
  border-radius: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const CardHeader = styled.div`
  margin-bottom: 12px;
  padding-right: 100px;
`;

const ProgramName = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const OrderId = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: #9ca3af;
`;

const OptionTag = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: linear-gradient(135deg, #e0edff 0%, #f0f7ff 100%);
  color: #1a73e8;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DetailIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #f9fafb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`;

const DetailText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #374151;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

const PriceTag = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a73e8;
`;

const ViewButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  color: white;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.35);
  }
`;

// Not Logged In Styles
const NotLoggedInContainer = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const NotLoggedInCard = styled.div`
  width: 100%;
  max-width: 380px;
  background: white;
  border-radius: 24px;
  padding: 40px 28px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const NotLoggedInIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const NotLoggedInTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
`;

const NotLoggedInText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 24px 0;
`;

const NotLoggedInFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 28px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FeatureIcon = styled.span`
  width: 24px;
  height: 24px;
  background: #e8f5e9;
  color: #34a853;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const FeatureText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #333;
`;

const NotLoggedInButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 14px;
  background: #1a1a1a;
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 14px;
  background: transparent;
  color: #1a1a1a;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1a1a1a;
    background: #f5f5f5;
  }
`;

// Empty State Styles
const EmptyStateContainer = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const EmptyStateCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 24px;
  padding: 40px 28px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const EmptyStateIconWrapper = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

const EmptyStateTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
`;

const EmptyStateText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 28px 0;
`;

const ExploreButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(26, 115, 232, 0.35);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;
